import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useRecaptcha } from "@/lib/useRecaptcha";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { execute } = useRecaptcha();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const token = await execute("forgot_password");
      await axios.post(`${API}/auth/forgot-password`, { email, recaptcha_token: token });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
          </div>
          <span className="font-heading font-bold text-[15px] text-[#0A2342]">Dive Media Strategist&trade;</span>
        </Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            {done ? (
              <div data-testid="forgot-password-sent">
                <div className="w-12 h-12 rounded-full bg-[#4FD1C5]/15 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-6 h-6 text-[#0F766E]" />
                </div>
                <div className="dms-label mb-3">Check your inbox</div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-3">You&apos;re on the way back in.</h1>
                <p className="text-[#475569] leading-relaxed">
                  If <span className="font-semibold text-[#0A2342]">{email}</span> is on file, we&apos;ve sent a reset link. It&apos;s good for the next hour.
                </p>
                <p className="text-[13.5px] text-[#64748B] mt-4">
                  Nothing arrived after a minute? Check your spam folder, then try again — some inboxes deliver in bursts.
                </p>
                <Link to="/login" data-testid="forgot-password-back-to-login" className="mt-8 inline-flex text-sm font-semibold text-[#1D4E89] hover:text-[#0A2342]">
                  &larr; Back to sign in
                </Link>
              </div>
            ) : (
              <div>
                <div className="w-12 h-12 rounded-full bg-[#0A2342]/6 flex items-center justify-center mb-5">
                  <Mail className="w-6 h-6 text-[#0A2342]" />
                </div>
                <div className="dms-label mb-3">Password reset</div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-2">Forgot your password?</h1>
                <p className="text-[#475569] mb-8">Enter your email and we&apos;ll send you a reset link.</p>

                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="dms-label block mb-2">Email</label>
                    <input
                      data-testid="forgot-password-email"
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="dms-input"
                      placeholder="you@ocean.com"
                      autoFocus
                    />
                  </div>
                  <button data-testid="forgot-password-submit" type="submit" disabled={busy} className="w-full dms-btn-primary">
                    {busy ? "Sending…" : "Send reset link"}
                  </button>
                </form>
                <p className="mt-6 text-sm text-[#475569]">
                  Remember it?{" "}
                  <Link to="/login" data-testid="forgot-password-to-login" className="font-semibold text-[#1D4E89] hover:text-[#0A2342]">Sign in</Link>
                </p>
                <p className="mt-1 text-[13.5px] text-[#64748B]">
                  Signed up with Google? Just use the &quot;Continue with Google&quot; button — there&apos;s no password to reset.
                </p>
                <p className="mt-4 text-[11px] text-[#94A3B8]">This site is protected by reCAPTCHA.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1682687982502-1529b3b33f85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxzY3ViYSUyMGRpdmVyJTIwdW5kZXJ3YXRlciUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4MTQ2MTM0MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2342]/60 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Doctrine</div>
          <p className="mt-3 text-xl font-heading font-semibold leading-snug">Locked out? A calm inbox check beats a panicked retry.</p>
        </div>
      </div>
    </div>
  );
}
